/**
 * Page 2 — Assessment Profile / Engagement Overview (v8 mock parity).
 *
 * Top: thin navy strip running header with client + assessment label and
 * methodology on the right. Gold hairline below.
 *
 * Body:
 *   ASSESSMENT PROFILE  (gold eyebrow)
 *   Engagement Overview (large dark title)
 *   <intro paragraph>
 *
 *   Card grid:
 *     ┌─ ORGANIZATION ─────────────────────────┐  (navy band)
 *     │ CLIENT ORGANIZATION │ PARENT ORGANIZ.  │  (light grey rows w/ label/value)
 *     │ HEADQUARTERS        │ REGION           │
 *     │ SECTOR              │ IFC CONTACT      │
 *     ├─ ENGAGEMENT ───────────────────────────┤
 *     │ ENGAGEMENT          │ METHODOLOGY      │
 *     │ CONDUCTED BY        │ FORMAT           │
 *     │ PRIMARY CONTACT     │                  │
 *     └────────────────────────────────────────┘
 */
import type jsPDF from "jspdf";
import { PAGE_MARGIN, REPORT_THEME, formatEngagementPeriod } from "./theme";
import { drawRunningHeader, drawPageFooter, drawSectionTitle } from "./pageChrome";

export interface AssessmentProfile {
  client_organization?: string | null;
  parent_organization?: string | null;
  sector?: string | null;
  region?: string | null;
  headquarters_location?: string | null;
  primary_contact?: string | null;
  ifc_contact?: string | null;
  assessment_conducted_by?: string | null;
  methodology?: string | null;
  format?: string | null;
  engagement_start_date?: string | null;
  engagement_end_date?: string | null;
  conducted_on_behalf_of?: string | null;
}

export interface InfrastructureScope {
  telecom_service_model?: string[] | null;
  components_present?: string[] | null;
  components_assessed?: string[] | null;
  scope_notes?: string | null;
}

export function renderAssessmentProfile(
  doc: jsPDF,
  profile: AssessmentProfile | null,
  ctx?: {
    clientOrgName?: string;
    methodology?: string | null;
    frameworks?: string[] | null;
    pageNumber?: number;
    scope?: InfrastructureScope | null;
  },
): void {
  doc.addPage();
  const pageW = doc.internal.pageSize.getWidth();

  const clientName = ctx?.clientOrgName || profile?.client_organization || "Client";
  const methodology = ctx?.methodology || profile?.methodology || "";
  drawRunningHeader(doc, {
    clientName,
    frameworks: ctx?.frameworks ?? null,
    methodology,
  });

  let y = drawSectionTitle(doc, {
    sectionNumber: "02",
    title: "Assessment Profile",
  });

  // Intro paragraph
  doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  const intro = `This Cybersecurity Risk Assessment Report evaluates ${clientName}'s cybersecurity posture across eight core domains: Risk Management, Governance, Supply Chain Management, Asset Management, Identity-Centric Security, Security Operations, Data Protection, and Infrastructure Security. The assessment aligns with internationally recognized frameworks and standards, including the NIST Cybersecurity Framework (NIST CSF), and applicable regulatory requirements.`;
  const introLines = doc.splitTextToSize(intro, pageW - PAGE_MARGIN * 2);
  doc.text(introLines, PAGE_MARGIN, y, { lineHeightFactor: 1.45 } as any);
  y += introLines.length * 14 + 18;

  // ── Card with sections ──
  const cardX = PAGE_MARGIN;
  const cardW = pageW - PAGE_MARGIN * 2;
  const cardTop = y;

  const orgRows: Array<[string, string, string, string]> = [
    ["CLIENT ORGANIZATION", profile?.client_organization || "—", "PARENT ORGANIZATION", profile?.parent_organization || "—"],
    ["HEADQUARTERS", profile?.headquarters_location || "—", "REGION", profile?.region || "—"],
    ["SECTOR", profile?.sector || "—", "CONDUCTED ON BEHALF OF", profile?.conducted_on_behalf_of || "—"],
  ];
  const engRows: Array<[string, string, string, string]> = [
    ["ENGAGEMENT PERIOD", formatEngagementPeriod(profile?.engagement_start_date, profile?.engagement_end_date), "METHODOLOGY", profile?.methodology || "—"],
    ["CONDUCTED BY", profile?.assessment_conducted_by || "—", "FORMAT", profile?.format || "—"],
  ];
  const contactRows: Array<[string, string, string, string]> = [
    ["PRIMARY CONTACT", profile?.primary_contact || "—", "ENGAGEMENT CONTACT", profile?.ifc_contact || "—"],
  ];

  let cur = cardTop;
  cur = drawSectionBand(doc, "ORGANIZATION DETAILS", cardX, cur, cardW);
  cur = drawCellRows(doc, orgRows, cardX, cur, cardW);
  cur = drawSectionBand(doc, "ENGAGEMENT DETAILS", cardX, cur, cardW);
  cur = drawCellRows(doc, engRows, cardX, cur, cardW);
  cur = drawSectionBand(doc, "CONTACT DETAILS", cardX, cur, cardW);
  cur = drawCellRows(doc, contactRows, cardX, cur, cardW);

  // Outer card border (lighter accent blue, more visible)
  doc.setDrawColor(...REPORT_THEME.accent.rgb);
  doc.setLineWidth(1.4);
  doc.rect(cardX, cardTop, cardW, cur - cardTop, "S");

  // Assessment Scope intentionally omitted here — it is rendered within the
  // Assessment Results by Domain section to avoid duplicate, overflowing content.

  drawPageFooter(doc, ctx?.pageNumber ?? 2);
}

function drawSectionBand(doc: jsPDF, label: string, x: number, y: number, w: number): number {
  const h = 24;
  doc.setFillColor(...REPORT_THEME.accent.rgb);
  doc.rect(x, y, w, h, "F");
  // Gold accent strip on the left for consistency with other sections.
  doc.setFillColor(...REPORT_THEME.gold.rgb);
  doc.rect(x, y, 3, h, "F");
  doc.setTextColor(...REPORT_THEME.textOnNavy.rgb);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(label, x + 14, y + 16);
  return y + h;
}

function drawCellRows(
  doc: jsPDF,
  rows: Array<[string, string, string, string]>,
  x: number,
  y: number,
  w: number,
): number {
  const rowH = 60;
  const colW = w / 2;
  rows.forEach((row) => {
    doc.setFillColor(255, 255, 255);
    doc.rect(x, y, w, rowH, "F");

    drawCell(doc, row[0], row[1], x, y, colW, rowH);
    if (row[2]) drawCell(doc, row[2], row[3], x + colW, y, colW, rowH);

    // Vertical divider between columns
    doc.setDrawColor(...REPORT_THEME.border.rgb);
    doc.setLineWidth(0.6);
    doc.line(x + colW, y + 8, x + colW, y + rowH - 8);

    // Bottom hairline
    doc.line(x, y + rowH, x + w, y + rowH);
    y += rowH;
  });
  return y;
}

function drawCell(doc: jsPDF, label: string, value: string, x: number, y: number, w: number, h: number): void {
  // Uppercase muted label — small, consistent typography
  doc.setTextColor(...REPORT_THEME.textMuted.rgb);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text(label, x + 16, y + 20);

  // Value — navy, semi-bold, larger size for hierarchy
  doc.setTextColor(...REPORT_THEME.navy.rgb);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11.5);
  const valLines = doc.splitTextToSize(value || "—", w - 32);
  doc.text(valLines.slice(0, 2), x + 16, y + 40);
}

